import {
  AutoForm,
  AutoFormCheckboxGroupField,
  Button,
  Checkbox
} from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { FC, ReactNode } from "react";
import Translate from "src/ui/components/Translate/Translate";
import { z } from "zod";
import ResponsiveImage, {
  imageFromSrc
} from "../ResponsiveImage/ResponsiveImage";
import { toast, useBloc } from "src/state/state";
import UserCubit from "src/state/UserCubit/UserCubit";
import reportErrorSentry from "src/lib/reportErrorSentry";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { APP_BREAKPOINT } from "src/constants/layout";
import { translate } from "lib/blocs/TranslateionBloc";
import { getHeadingProps, useFocusHeading } from "src/lib/useFocusHeading";
import { useBlocNext } from "@blac/react";
import { cmsImageUrl } from "src/lib/cmsImageUrl";

const CenterUi = styled.div`
  padding: 0.7rem 0.4rem 0;
  .responsive-image {
    aspect-ratio: 2.5;
    margin-bottom: 1rem;
    width: 100%;
  }
`;

const ContentWrap = styled.div`
  color: var(--greys-charcoal-80, rgba(33, 33, 33, 0.8));
  margin-top: 0.8rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    margin-top: 1.5rem;
  }
`;

const Paragraph = styled.p`
  margin: 0 0 0.8rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    margin: 0 0 1.5rem;
  }

  &[data-register="true"] {
    text-align: center;
  }
`;

const ButtonPosition = styled.div`
  display: flex;
  justify-content: end;
  margin-bottom: -1.4rem;
  margin-top: 1rem;
`;

const ConsentFormSchema = z.object({
  consentSet1: z.array(z.literal("consentSet1"), {
    message: translate("registerConsent.set1", { context: "error" })
  }),
  consentSet2: z.array(z.literal("consentSet2"), {
    message: translate("registerConsent.set2", { context: "error" })
  })
});

const ConsentForm: FC<{
  onSuccess?: () => unknown;
  onLoadingChange?: (loading: boolean) => void;
  onError?: () => void;
  header?: ReactNode;
  isRegistering?: boolean;
  openPopup?: boolean;
}> = (props) => {
  const [, { acceptConsents }] = useBloc(UserCubit);
  const [, { firstName }] = useBlocNext(UserPreferencesCubit);
  const { headingRef } = useFocusHeading();

  const handleSubmit = (): void => {
    props.onLoadingChange?.(true);
    void acceptConsents()
      .then(async () => {
        await props.onSuccess?.();
      })
      .catch((e: unknown) => {
        props.onLoadingChange?.(false);
        props.onError?.();
        toast.error("error_generic");
        if (e instanceof Error) {
          reportErrorSentry(e.message);
        } else {
          reportErrorSentry(e);
        }
      });
  };

  const handleButtonClick = () => {
    // Wait for validation to render errors, then focus the first invalid field
    setTimeout(() => {
      const firstErrorField = document.querySelector(
        "[aria-invalid='true'], .error-class"
      );
      if (firstErrorField) {
        (firstErrorField as HTMLElement).focus();
      }
    }, 100);
  };

  return (
    <CenterUi>
      {props.header}
      <AutoForm schema={ConsentFormSchema} onSubmit={handleSubmit}>
        {!props.isRegistering && (
          <ResponsiveImage
            image={imageFromSrc(
              cmsImageUrl(
                "c8f0e589ce45b22b920a91bc573a4d170e771ace-1750x875.webp"
              )
            )}
          />
        )}
        {props.isRegistering && (
          <>
            <h1
              className="as-h4-large"
              ref={headingRef}
              {...getHeadingProps({ textAlign: "center" })}
            >
              <Translate msg="consents_update_title_registration" />
            </h1>
          </>
        )}
        <ContentWrap>
          <Paragraph data-register={props.isRegistering}>
            <Translate
              msg={
                props.isRegistering
                  ? "consents_update_description_registration"
                  : "consents_update_description"
              }
              variables={{ nickname: firstName }}
            />
          </Paragraph>

          <br />
          <AutoFormCheckboxGroupField name="consentSet1">
            <Checkbox value={"consentSet1"}>
              <Translate
                msg="registerConsent.set1"
                variables={{
                  context: props.openPopup ? "popup" : undefined
                }}
              />
            </Checkbox>
          </AutoFormCheckboxGroupField>
          <br />
          <AutoFormCheckboxGroupField name="consentSet2">
            <Checkbox value={"consentSet2"}>
              <Translate
                msg="registerConsent.set2"
                variables={{
                  context: props.openPopup ? "popup" : undefined
                }}
              />
            </Checkbox>
          </AutoFormCheckboxGroupField>

          <ButtonPosition className="button-position">
            <Button
              type="submit"
              onClick={handleButtonClick}
              theme={props.isRegistering ? "sunrise" : "charcoal"}
            >
              <Translate msg="confirm" />
            </Button>
          </ButtonPosition>
        </ContentWrap>
      </AutoForm>
    </CenterUi>
  );
};

export default ConsentForm;
